import React from 'react';
import { Field } from 'react-final-form';

import { MultiSelectCombobox } from '../../components';

import css from './FieldProvinciasSelect.module.css';

/**
 * FASE2-WIZARD-2b — Provincias del mandato de compra.
 *
 * Adaptador de react-final-form sobre MultiSelectCombobox, el MISMO control que
 * usa el filtro de provincia del buscador. Aquí solo viven las reglas de
 * negocio; la interacción (buscador, panel, casillas) es compartida.
 *
 * Reglas:
 *  - "Toda España" es EXCLUSIVA: al elegirla se limpia lo demás y queda sola.
 *    Mientras esté puesta, ninguna provincia es elegible.
 *  - El bloqueo es REVERSIBLE: al quitarla se vuelve a poder elegir provincias.
 *  - Máximo 3 provincias. Al llegar al tope, se avisa dentro del panel y las no
 *    seleccionadas dejan de ser elegibles hasta que se quite una.
 *  - Obligatorio: sin ámbito no se avanza (la validación la pone el llamante).
 *
 * El tope se aplica con las opciones DESHABILITADAS, no ocultándolas: ver la
 * lista completa en gris explica por qué no se puede seguir; hacerla desaparecer
 * parecería un fallo.
 */
export const TODA_ESPANA = 'toda-espana';
export const MAX_PROVINCIAS = 3;

/**
 * Calcula la selección resultante aplicando la exclusividad de "Toda España".
 * Se exporta para poder probarla sin montar el componente.
 *
 * @param {Array<string>} next selección que propone el combobox
 * @param {Array<string>} current selección previa
 * @returns {Array<string>} selección ya normalizada
 */
export const applyProvinciasRules = (next = [], current = []) => {
  const addedTodaEspana = next.includes(TODA_ESPANA) && !current.includes(TODA_ESPANA);
  if (addedTodaEspana) {
    // Acaba de marcarla: se queda sola, se limpia cualquier provincia previa.
    return [TODA_ESPANA];
  }
  if (next.includes(TODA_ESPANA) && next.length > 1) {
    // Estaba puesta y se ha intentado añadir otra cosa: gana la exclusiva.
    return [TODA_ESPANA];
  }
  return next.slice(0, MAX_PROVINCIAS);
};

const FieldProvinciasSelect = props => {
  const {
    name,
    label,
    options = [],
    placeholder,
    searchPlaceholder,
    emptyLabel,
    clearLabel,
    doneLabel,
    helpText,
    maxNotice,
    validate,
  } = props;

  return (
    <Field name={name} validate={validate}>
      {({ input, meta }) => {
        const value = Array.isArray(input.value) ? input.value : [];
        const hasTodaEspana = value.includes(TODA_ESPANA);
        const isFull = !hasTodaEspana && value.length >= MAX_PROVINCIAS;

        const isOptionDisabled = opt => {
          // "Toda España" SIEMPRE es elegible, incluso con el tope alcanzado:
          // es la salida que ofrece el texto de ayuda ("si buscas en más zonas,
          // márcala"), y al elegirla limpia las provincias. Deshabilitarla al
          // llegar a 3 cerraba justo la puerta que se le está señalando.
          if (opt.value === TODA_ESPANA) {
            return false;
          }
          const selected = value.includes(opt.value);
          if (selected) {
            // Lo ya elegido siempre se puede quitar: si no, el bloqueo sería
            // definitivo y el usuario quedaría atrapado.
            return false;
          }
          if (hasTodaEspana) {
            // Con la exclusiva puesta no se añade nada más, pero ella misma
            // sigue siendo desmarcable por la rama de arriba.
            return true;
          }
          return isFull;
        };

        const showError = meta.touched && meta.error;

        return (
          <div className={css.root}>
            <span className={css.label}>{label}</span>
            <MultiSelectCombobox
              options={options}
              value={value}
              onChange={next => input.onChange(applyProvinciasRules(next, value))}
              onBlur={() => input.onBlur()}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              emptyLabel={emptyLabel}
              clearLabel={clearLabel}
              doneLabel={doneLabel}
              isOptionDisabled={isOptionDisabled}
              notice={isFull ? maxNotice : null}
              helpText={helpText}
              invalid={!!showError}
            />
            {showError ? <p className={css.error}>{meta.error}</p> : null}
          </div>
        );
      }}
    </Field>
  );
};

export default FieldProvinciasSelect;
